import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

const handleComingSoon = (label) => () => {
  toast('🔜 ' + label + ' disponível em breve!', { icon: '🚀', duration: 3000 });
};

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const loadingToast = toast.loading('Authenticating...');

    try {
      const response = await api.post('/auth/login', { email, password });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      toast.dismiss(loadingToast);
      toast.success(`Welcome back, ${response.data.user.name}!`);

      navigate('/app/dashboard');
    } catch (error) {
      toast.dismiss(loadingToast);

      if (error.response?.data?.emailNotVerified) {
        toast.error(error.response.data.error);
        navigate('/verify', { state: { email: error.response.data.email } });
      } else {
        toast.error(error.response?.data?.error || 'Unable to log in.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_2fr] bg-slate-50 font-inter">

      {/* LEFT PANEL */}
      <div className="hidden lg:flex flex-col justify-between bg-indigo-700 text-white p-12 lg:p-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-indigo-800 opacity-90 z-0"></div>

        <div className="relative z-10">
          <div className="flex items-center space-x-2 mb-16">
            <span className="material-symbols-outlined text-3xl">account_balance_wallet</span>
            <span className="text-xl font-bold font-manrope tracking-tight">FinControl</span>
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold font-manrope leading-[1.1] mb-6">
            Sua clareza<br />
            financeira<br />
            começa<br />
            aqui ✨
          </h1>

          <p className="text-indigo-200 text-lg leading-relaxed max-w-sm">
            Tenha controle total do seu dinheiro com uma plataforma simples e inteligente.
            Organize suas finanças, acompanhe seus resultados e tome decisões com confiança.
          </p>

          <div className="relative z-10 mt-12 bg-indigo-800/40 backdrop-blur-sm p-6 rounded-2xl border border-indigo-500/30">
            <div className="flex items-center space-x-3 mb-4 text-indigo-300">
              <span className="font-bold text-sm tracking-wide">O que nossos usuários estão dizendo</span>
            </div>
            <p className="text-sm text-indigo-200 leading-relaxed italic mb-6">
              "Agora eu consigo entender melhor pra onde meu dinheiro vai. Ficou muito mais fácil me organizar."
            </p>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-indigo-500/30 overflow-hidden flex items-center justify-center border border-indigo-400">
              </div>
              <div>
                <p className="font-bold text-sm text-white">João Silva</p>
                <p className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold">Usuário FinControl</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex flex-col min-h-screen relative overflow-y-auto">
        {/* Top bar with Smart Setup */}
        <div className="absolute top-6 right-6 lg:top-8 lg:right-12 hidden sm:flex items-center space-x-4 bg-white px-4 py-3 rounded-xl shadow-sm border border-slate-100">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-primary flex items-center justify-center">
            <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">Configuração Inteligente</p>
            <p className="text-[10px] text-slate-500 font-medium">Sincronize seu banco em segundos</p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-[480px]">
            {/* Mobile Logo */}
            <div className="flex lg:hidden items-center space-x-2 mb-10 text-primary">
              <span className="material-symbols-outlined text-3xl">account_balance_wallet</span>
              <span className="text-2xl font-bold font-manrope tracking-tight text-slate-900">FinControl</span>
            </div>

            <div className="mb-10">
              <h2 className="text-3xl font-bold font-manrope text-slate-900 mb-2">Acesse sua conta</h2>
              <p className="text-slate-500 font-medium">Entre para visualizar e controlar suas finanças.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-slate-200/50 border-transparent rounded-xl px-4 py-3 text-sm font-medium text-slate-900 placeholder-slate-400 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="jane.doe@architect.com" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest text-left">Senha</label>
                  <Link to="/forgot-password" className="text-[10px] font-bold text-primary hover:underline uppercase tracking-widest text-right">Esqueceu?</Link>
                </div>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-slate-200/50 border-transparent rounded-xl px-4 py-3 text-sm font-medium text-slate-900 placeholder-slate-400 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all pr-12" placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <span className="material-symbols-outlined text-[18px]">{showPassword ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
              </div>

              <button type="submit" disabled={isLoading} className="w-full bg-[#4f46e5] text-white font-bold py-3.5 px-4 rounded-xl shadow-[0_8px_16px_-6px_rgba(79,70,229,0.4)] hover:bg-[#4338ca] hover:shadow-[0_12px_20px_-8px_rgba(79,70,229,0.5)] hover:-translate-y-[1px] transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none mt-2">
                {isLoading ? 'Authenticating...' : 'Entrar no FinControl'}
              </button>
            </form>

            <div className="mt-10 mb-8 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs font-bold tracking-widest uppercase">
                <span className="bg-slate-50 px-4 text-slate-400">Ou entre com</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button type="button" onClick={handleComingSoon('Login com Google')} className="flex items-center justify-center space-x-2 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm">
                <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
                <span className="text-sm font-bold text-slate-700">Google</span>
              </button>
              <button type="button" onClick={handleComingSoon('Login SSO')} className="flex items-center justify-center space-x-2 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm">
                <span className="material-symbols-outlined text-[18px] text-slate-700">passkey</span>
                <span className="text-sm font-bold text-slate-700">SSO</span>
              </button>
            </div>

            <p className="text-center mt-10 text-sm font-medium text-slate-500">
              Novo por aqui? <Link to="/register" className="font-bold text-primary hover:underline ml-1">Criar conta</Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-12 py-8 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          <p>© {new Date().getFullYear()} FINCONTROL INC.</p>
          <div className="flex space-x-6">
            <a href="#support" className="hover:text-slate-600 transition-colors">Suporte</a>
            <a href="#status" className="hover:text-slate-600 transition-colors">Status</a>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Login;
